'use client';

import { useEffect, useSyncExternalStore } from 'react';

/**
 * A page that brings its own bottom row, so the Mucka Bar stands down.
 *
 * A certificate on a phone in glass is the first (Tom, 2026-10-16): its dock
 * holds Back, To do and Save & exit, and with the tab bar and the M button
 * underneath it the foot of the screen was two rows of buttons, only one of
 * which belonged to the job in hand. The dock takes the bar's place, and while
 * the certificate is listening, its pill sits on the row above, the way a
 * minimised Mucka chat sits above the Ask Mucka box.
 *
 * Not `useCoverChrome`, on purpose. That is for a surface covering the whole
 * app (the Face ID lock) and it also switches the iPhone's back swipe off,
 * which a page you can leave must keep.
 *
 * `body.mucka-bar-yielded` lets CSS stop reserving the bar's height: the dock
 * clears `--mucka-navbar-clear`, which is the bar plus a gap, and with no bar
 * that gap is all that is left. Ref-counted like `useBottomBarClaim`, so a
 * second page component claiming the same thing cannot clear the first's.
 */

let yields = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of listeners) fn();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

const getSnapshot = (): boolean => yields > 0;
// The page decides on the client, so the server always draws the bar.
const getServerSnapshot = (): boolean => false;

/** While `active`, this page owns the bottom row and the Mucka Bar is not drawn. */
export function useYieldBar(active: boolean): void {
  useEffect(() => {
    if (!active || typeof document === 'undefined') return;
    yields += 1;
    document.body.classList.add('mucka-bar-yielded');
    emit();
    return () => {
      yields = Math.max(0, yields - 1);
      if (yields === 0) document.body.classList.remove('mucka-bar-yielded');
      emit();
    };
  }, [active]);
}

/** True while a page has taken the bottom row. Read by the bar. */
export function useBarYielded(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
